import type { CreateUserBody, IssuedPassword, Permissions, Role, UserEntry } from '@obras/shared';
import {
  ALL_PERMISSIONS,
  ASSIGNABLE_ROLES,
  DEFAULT_PASSWORD,
  defaultPermissionsFor,
  formatDateTime,
  normalizePermissions,
  OBRA_EDIT_SCOPE_HINTS,
  OBRA_EDIT_SCOPE_LABELS,
  OBRA_EDIT_SCOPES,
  obraEditScope,
  PERMISSION_GROUPS,
  PERMISSION_HINTS,
  PERMISSION_LABELS,
  ROLE_LABELS,
  withObraEditScope,
  type PermissionKey,
} from '@obras/shared';
import {
  Ban,
  Check,
  CircleCheck,
  KeyRound,
  RotateCcw,
  Settings,
  ShieldCheck,
  type LucideIcon,
  UserPlus,
  UserRound,
} from 'lucide-react';
import { useState, type FormEvent } from 'react';

import { PageHeader } from '../components/AppShell.js';
import { Badge, type Tone } from '../components/ui/Badge.js';
import { Button } from '../components/ui/Button.js';
import { Card } from '../components/ui/Card.js';
import {
  Checkbox,
  RadioGroup,
  SearchInput,
  SelectField,
  TextField,
} from '../components/ui/Field.js';
import { Avatar, EmptyState, ErrorState, FormError, Skeleton } from '../components/ui/Feedback.js';
import { Modal } from '../components/ui/Modal.js';
import { PasswordBox, PasswordValue } from '../components/ui/PasswordBox.js';
import { Tab, TabList } from '../components/ui/Tabs.js';
import {
  useCreateUser,
  useResetPassword,
  useUpdateUser,
  useUsers,
} from '../features/usuarios/api.js';
import { ApiError } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';

const ICON = { strokeWidth: 1.5 } as const;

const STATUS_STYLE: Record<UserEntry['status'], { tone: Tone; label: string }> = {
  active: { tone: 'success', label: 'Ativo' },
  invited: { tone: 'warning', label: 'Aguardando senha' },
  suspended: { tone: 'danger', label: 'Desativado' },
};

const ROLE_TONE: Record<Role, Tone> = {
  owner: 'primary',
  admin: 'primary',
  gestor: 'info',
  fiscal: 'muted',
  leitor: 'muted',
};

/** As caixas que contam no "x de y" da tabela: a sub-opção não é uma caixa. */
const CAIXINHAS: readonly PermissionKey[] = PERMISSION_GROUPS.flatMap((grupo) => grupo.keys);

const OPCOES_DE_ALCANCE = OBRA_EDIT_SCOPES.map((scope) => ({
  value: scope,
  label: OBRA_EDIT_SCOPE_LABELS[scope],
  hint: OBRA_EDIT_SCOPE_HINTS[scope],
}));

/**
 * As caixas de acesso, agrupadas como a pessoa pensa nelas: o que se faz na
 * obra de um lado, cadastros e páginas do outro.
 *
 * "Editar obras" carrega a sub-opção de alcance (ADR 052): padrão alcança a
 * obra da pessoa, global alcança qualquer uma. Ela só aparece com a caixa
 * marcada, e desmarcar a caixa a desfaz — global sem a caixa não existe.
 */
function PermissionFields({
  value,
  onChange,
  disabled = false,
}: {
  value: Permissions;
  onChange: (next: Permissions) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex flex-col gap-6">
      {PERMISSION_GROUPS.map((grupo) => (
        <fieldset key={grupo.title} className="flex flex-col gap-3">
          <legend className="mb-1 text-[13px] font-medium">{grupo.title}</legend>

          {grupo.keys.map((key) => (
            <div key={key} className="flex flex-col gap-3">
              <Checkbox
                label={PERMISSION_LABELS[key]}
                hint={PERMISSION_HINTS[key]}
                checked={value[key]}
                disabled={disabled}
                onChange={(event) =>
                  onChange(normalizePermissions({ ...value, [key]: event.target.checked }))
                }
              />

              {key === 'canEditObras' && value.canEditObras ? (
                /* Recuada à largura da caixa mais o espaço, para ler como filha dela. */
                <div className="ml-7">
                  <RadioGroup
                    label="Alcance de editar obras"
                    value={obraEditScope(value)}
                    options={OPCOES_DE_ALCANCE}
                    disabled={disabled}
                    onChange={(scope) => onChange(withObraEditScope(value, scope))}
                  />
                </div>
              ) : null}
            </div>
          ))}
        </fieldset>
      ))}

      {disabled ? (
        <p className="text-[13px] text-ink-3">
          O administrador do órgão tem acesso a todas as páginas. Não há permissões a conceder.
        </p>
      ) : null}
    </div>
  );
}

type AbaDoUsuario = 'informacoes' | 'acessos' | 'senha';

interface Aba {
  readonly id: AbaDoUsuario;
  readonly rotulo: string;
  readonly icone: LucideIcon;
}

const ABA_INFORMACOES: Aba = { id: 'informacoes', rotulo: 'Informações', icone: UserRound };
const ABA_ACESSOS: Aba = { id: 'acessos', rotulo: 'Acessos', icone: ShieldCheck };
const ABA_SENHA: Aba = { id: 'senha', rotulo: 'Senha', icone: KeyRound };

/** Campos que moram na aba de informações, para acender o aviso na aba certa. */
const CAMPOS_DE_INFORMACOES = ['name', 'email', 'role', 'jobTitle'];

function abasComErro(fieldErrors: Record<string, string>): Set<AbaDoUsuario> {
  const abas = new Set<AbaDoUsuario>();
  if (CAMPOS_DE_INFORMACOES.some((campo) => fieldErrors[campo])) abas.add('informacoes');
  if (Object.keys(fieldErrors).some((campo) => campo.startsWith('permissions'))) {
    abas.add('acessos');
  }
  return abas;
}

/** Depois de um erro do servidor, leva para a primeira aba que tem campo a corrigir. */
function irParaAbaComErro(
  erros: Record<string, string>,
  atual: AbaDoUsuario,
  setAba: (aba: AbaDoUsuario) => void,
): void {
  const comErro = abasComErro(erros);
  const [primeira] = comErro;
  if (primeira && !comErro.has(atual)) setAba(primeira);
}

/**
 * A trilha lateral do modal de usuário.
 *
 * Três assuntos que não se misturam: quem a pessoa é, o que ela alcança e como
 * ela entra. Numa coluna só, o gestor rolava por dez caixas para chegar ao
 * botão de senha; com a trilha, cada aba responde a uma pergunta — o mesmo
 * desenho do formulário de obra.
 */
function TrilhaDoUsuario({
  abas,
  atual,
  comErro,
  onChange,
}: {
  abas: readonly Aba[];
  atual: AbaDoUsuario;
  comErro: ReadonlySet<AbaDoUsuario>;
  onChange: (aba: AbaDoUsuario) => void;
}) {
  return (
    <nav className="sm:sticky sm:top-0 sm:self-start">
      <TabList label="Seções do usuário" orientation="vertical">
        {abas.map((aba) => (
          <Tab
            key={aba.id}
            vertical
            active={atual === aba.id}
            alert={comErro.has(aba.id)}
            icon={aba.icone}
            onClick={() => onChange(aba.id)}
          >
            {aba.rotulo}
          </Tab>
        ))}
      </TabList>
    </nav>
  );
}

/*
 * Trilha à esquerda, conteúdo à direita; altura fixa a partir de `sm` para o
 * modal não pular ao trocar de aba — o mesmo desenho do formulário de obra.
 */
const CORPO_EM_ABAS = 'grid gap-6 px-6 pb-2 sm:h-[480px] sm:grid-cols-[180px_1fr]';
const COLUNA_DE_CONTEUDO = 'flex min-w-0 flex-col gap-4 sm:overflow-y-auto sm:pr-1';

function CreateUserModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const create = useCreateUser();

  const [aba, setAba] = useState<AbaDoUsuario>('informacoes');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('fiscal');
  const [jobTitle, setJobTitle] = useState('');
  const [permissions, setPermissions] = useState<Permissions>(defaultPermissionsFor('fiscal'));
  const [issued, setIssued] = useState<IssuedPassword | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const administra = role === 'admin';

  function close(): void {
    setIssued(null);
    setAba('informacoes');
    setName('');
    setEmail('');
    setJobTitle('');
    setRole('fiscal');
    setPermissions(defaultPermissionsFor('fiscal'));
    setFormError(null);
    setFieldErrors({});
    onClose();
  }

  /*
   * Trocar o papel devolve os acessos ao padrão dele. Quem escolhe "gestor"
   * espera o gestor completo, não o que sobrou das caixas do fiscal; o ajuste
   * fino vem depois, caixa a caixa.
   */
  function trocarPapel(proximo: Role): void {
    setRole(proximo);
    setPermissions(defaultPermissionsFor(proximo));
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setFormError(null);
    setFieldErrors({});

    const body: CreateUserBody = {
      name,
      email,
      role,
      jobTitle: jobTitle.trim() === '' ? undefined : jobTitle,
      permissions,
    };

    try {
      setIssued(await create.mutateAsync(body));
    } catch (error) {
      if (error instanceof ApiError) {
        const erros = error.fieldErrors();
        setFieldErrors(erros);
        setFormError(error.details ? 'Verifique os campos destacados.' : error.message);
        irParaAbaComErro(erros, aba, setAba);
      } else {
        setFormError('Não foi possível cadastrar o usuário.');
      }
    }
  }

  return (
    <Modal
      open={open}
      onClose={close}
      size="lg"
      flush={issued === null}
      title={issued ? 'Usuário cadastrado' : 'Novo usuário'}
      description={
        issued
          ? 'Entregue a senha à pessoa e peça que ela troque no primeiro acesso.'
          : `A pessoa entra com a senha padrão ${DEFAULT_PASSWORD} e a troca depois.`
      }
      footer={
        issued ? (
          <Button variant="primary" onClick={close}>
            Fechar
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={close} disabled={create.isPending}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              form="user-form"
              type="submit"
              loading={create.isPending}
              icon={<UserPlus {...ICON} size={16} />}
            >
              Cadastrar
            </Button>
          </>
        )
      }
    >
      {issued ? (
        <PasswordBox issued={issued} />
      ) : (
        <div className={CORPO_EM_ABAS}>
          <TrilhaDoUsuario
            abas={[ABA_INFORMACOES, ABA_ACESSOS]}
            atual={aba}
            comErro={abasComErro(fieldErrors)}
            onChange={setAba}
          />

          <form
            id="user-form"
            onSubmit={(event) => void handleSubmit(event)}
            className={COLUNA_DE_CONTEUDO}
            noValidate
          >
            <FormError>{formError}</FormError>

            {aba === 'informacoes' ? (
              <>
                <TextField
                  label="Nome completo"
                  required
                  autoFocus
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  {...(fieldErrors['name'] ? { error: fieldErrors['name'] } : {})}
                />

                <TextField
                  label="E-mail"
                  type="email"
                  required
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  {...(fieldErrors['email'] ? { error: fieldErrors['email'] } : {})}
                />

                <SelectField
                  label="Papel"
                  value={role}
                  onChange={(event) => trocarPapel(event.target.value as Role)}
                  hint="Fiscal atua nas obras em que for designado; gestor acompanha todo o portfólio. Trocar o papel devolve os acessos ao padrão dele."
                >
                  {ASSIGNABLE_ROLES.map((option) => (
                    <option key={option} value={option}>
                      {ROLE_LABELS[option]}
                    </option>
                  ))}
                </SelectField>

                <TextField
                  label="Cargo"
                  placeholder="Engenheiro Civil, Secretário de Obras…"
                  value={jobTitle}
                  onChange={(event) => setJobTitle(event.target.value)}
                />
              </>
            ) : (
              <PermissionFields
                value={administra ? ALL_PERMISSIONS : permissions}
                onChange={setPermissions}
                disabled={administra}
              />
            )}
          </form>
        </div>
      )}
    </Modal>
  );
}

/**
 * A aba da senha.
 *
 * Fica fora do formulário de propósito: redefinir a senha vale na hora, não ao
 * salvar. Um botão que só surte efeito depois do "Salvar" ao lado de outro que
 * surte na hora, no mesmo modal, é a receita para o gestor achar que redefiniu
 * quando não redefiniu — ou o contrário. Por isso é aba própria, e nela o
 * rodapé só fecha.
 */
function PasswordPanel({ user }: { user: UserEntry }) {
  const reset = useResetPassword();
  const [issued, setIssued] = useState<IssuedPassword | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  async function redefinir(): Promise<void> {
    setErro(null);
    try {
      setIssued(await reset.mutateAsync(user.id));
    } catch (error) {
      setErro(error instanceof ApiError ? error.message : 'Não foi possível redefinir a senha.');
    }
  }

  return (
    <section className="flex flex-col gap-4">
      <header>
        <h3 className="text-[15px] font-semibold tracking-tight">Senha</h3>
        <p className="mt-0.5 text-[13px] text-ink-3">
          Devolve a senha à padrão <span className="font-mono text-ink">{DEFAULT_PASSWORD}</span> e
          derruba as sessões abertas. Vale na hora, sem passar pelo Salvar.
        </p>
      </header>

      <FormError>{erro}</FormError>

      {issued?.password ? (
        <>
          <p className="text-[13px] text-ink-3">
            Senha redefinida. Diga a {user.name.split(' ')[0]} e peça que troque no primeiro acesso.
          </p>
          <PasswordValue password={issued.password} onSurface />
        </>
      ) : (
        <div>
          <Button
            variant="surface"
            loading={reset.isPending}
            icon={<RotateCcw {...ICON} size={16} />}
            onClick={() => void redefinir()}
          >
            Redefinir senha
          </Button>
        </div>
      )}
    </section>
  );
}

/**
 * O que a engrenagem abre.
 *
 * Trilha à esquerda com três abas. Informações e acessos se salvam juntos, pelo
 * rodapé; a senha age sozinha, e na aba dela o rodapé só fecha.
 */
function EditUserModal({ user, onClose }: { user: UserEntry | null; onClose: () => void }) {
  const update = useUpdateUser();
  const [aba, setAba] = useState<AbaDoUsuario>('informacoes');
  const [permissions, setPermissions] = useState<Permissions>(defaultPermissionsFor('fiscal'));
  const [role, setRole] = useState<Role>('fiscal');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [ready, setReady] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Carrega os valores do usuário na primeira renderização com ele aberto.
  if (user && !ready) {
    setPermissions(user.permissions);
    setRole(user.role);
    setName(user.name);
    setEmail(user.email);
    setReady(true);
  }

  function close(): void {
    setReady(false);
    setAba('informacoes');
    setFormError(null);
    setFieldErrors({});
    onClose();
  }

  const administra = role === 'admin' || role === 'owner';

  /* Trocar o papel devolve os acessos ao padrão dele — ver o cadastro. */
  function trocarPapel(proximo: Role): void {
    setRole(proximo);
    setPermissions(defaultPermissionsFor(proximo));
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!user) return;

    setFormError(null);
    setFieldErrors({});

    try {
      await update.mutateAsync({
        userId: user.id,
        body: {
          name: name.trim(),
          email: email.trim(),
          role: role === 'owner' ? undefined : role,
          permissions,
        },
      });
      close();
    } catch (error) {
      if (error instanceof ApiError) {
        const erros = error.fieldErrors();
        setFieldErrors(erros);
        setFormError(error.details ? 'Verifique os campos destacados.' : error.message);
        irParaAbaComErro(erros, aba, setAba);
      } else {
        setFormError('Não foi possível salvar as alterações.');
      }
    }
  }

  const situacao = user ? STATUS_STYLE[user.status] : null;

  return (
    <Modal
      open={user !== null}
      onClose={close}
      size="lg"
      flush
      title={user ? `Configurações de ${user.name}` : 'Configurações'}
      description="Dados de acesso, papel, páginas liberadas e senha."
      footer={
        aba === 'senha' ? (
          <Button variant="ghost" onClick={close}>
            Fechar
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={close} disabled={update.isPending}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              form="edit-user-form"
              type="submit"
              loading={update.isPending}
              icon={<Check {...ICON} size={16} />}
            >
              Salvar
            </Button>
          </>
        )
      }
    >
      <div className={CORPO_EM_ABAS}>
        <TrilhaDoUsuario
          abas={[ABA_INFORMACOES, ABA_ACESSOS, ABA_SENHA]}
          atual={aba}
          comErro={abasComErro(fieldErrors)}
          onChange={setAba}
        />

        {aba === 'senha' ? (
          user ? (
            <div className={COLUNA_DE_CONTEUDO}>
              {/* `key` zera a senha exibida ao trocar de pessoa sem fechar o modal. */}
              <PasswordPanel key={user.id} user={user} />
            </div>
          ) : null
        ) : (
          <form
            id="edit-user-form"
            onSubmit={(event) => void handleSubmit(event)}
            className={COLUNA_DE_CONTEUDO}
            noValidate
          >
            <FormError>{formError}</FormError>

            {aba === 'informacoes' ? (
              <>
                {user && situacao ? (
                  <dl className="flex flex-wrap gap-x-8 gap-y-3 text-[13px]">
                    <div className="min-w-0">
                      <dt className="text-ink-3">Último acesso</dt>
                      <dd className="tabular mt-0.5 font-mono text-[12px]">
                        {user.lastLoginAt ? formatDateTime(user.lastLoginAt) : 'nunca'}
                      </dd>
                    </div>
                    <div className="min-w-0">
                      <dt className="text-ink-3">Situação</dt>
                      <dd className="mt-0.5">
                        <Badge tone={situacao.tone}>{situacao.label}</Badge>
                      </dd>
                    </div>
                  </dl>
                ) : null}

                <TextField
                  label="Nome"
                  required
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  {...(fieldErrors['name'] ? { error: fieldErrors['name'] } : {})}
                />

                {/*
                  E-mail é a credencial de entrada da pessoa, que pertence a um órgão
                  só (ADR 057). Está aqui porque corrigir um e-mail digitado errado no
                  cadastro é justamente o que trava o primeiro acesso — e quem
                  cadastrou é quem percebe.
                */}
                <TextField
                  label="E-mail"
                  type="email"
                  required
                  hint="É com ele que a pessoa entra no sistema."
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  {...(fieldErrors['email'] ? { error: fieldErrors['email'] } : {})}
                />

                <SelectField
                  label="Papel"
                  value={role}
                  disabled={user?.role === 'owner'}
                  hint="Trocar o papel devolve os acessos ao padrão dele."
                  onChange={(event) => trocarPapel(event.target.value as Role)}
                >
                  {(user?.role === 'owner' ? (['owner'] as const) : ASSIGNABLE_ROLES).map(
                    (option) => (
                      <option key={option} value={option}>
                        {ROLE_LABELS[option]}
                      </option>
                    ),
                  )}
                </SelectField>
              </>
            ) : (
              <PermissionFields
                value={administra ? ALL_PERMISSIONS : permissions}
                onChange={setPermissions}
                disabled={administra}
              />
            )}
          </form>
        )}
      </div>
    </Modal>
  );
}

export function UsuariosPage() {
  const { user: current } = useAuth();
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  /*
   * Guarda o id, não a linha. Guardando a linha, o modal continuaria mostrando
   * o retrato do instante em que abriu — e depois de redefinir a senha o selo
   * ainda diria "aguardando senha", que o servidor acabou de desmentir.
   */
  const [editingId, setEditingId] = useState<string | null>(null);

  const { data, isPending, isError, error, refetch } = useUsers({
    search: search || undefined,
    perPage: 100,
  });

  const update = useUpdateUser();

  return (
    <>
      <PageHeader
        title="Usuários"
        description="Quem acessa o órgão, com que papel e com quais permissões."
        action={
          <Button
            variant="primary"
            icon={<UserPlus {...ICON} size={16} />}
            onClick={() => setCreateOpen(true)}
          >
            Novo usuário
          </Button>
        }
      />

      <div className="mb-4 flex justify-end">
        <div className="relative w-full sm:w-80">
          <SearchInput
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onClear={() => setSearch('')}
            placeholder="Buscar por nome ou e-mail"
            aria-label="Buscar usuários"
          />
        </div>
      </div>

      {isError ? (
        <ErrorState
          message={error instanceof Error ? error.message : 'Erro desconhecido.'}
          onRetry={() => void refetch()}
        />
      ) : isPending ? (
        <Skeleton className="h-96 rounded-none" />
      ) : data.items.length === 0 ? (
        <Card square>
          <EmptyState
            icon={UserPlus}
            title="Nenhum usuário"
            description="Cadastre as pessoas que vão acompanhar as obras deste órgão."
          />
        </Card>
      ) : (
        <Card square className="overflow-x-auto">
          <table className="w-full min-w-[880px] text-left">
            <caption className="sr-only">Usuários do órgão</caption>
            <thead className="bg-sunken">
              <tr className="text-[11px] font-medium tracking-[0.04em] text-ink-3 uppercase *:border-b-2 *:border-line-2">
                <th scope="col" className="px-5 py-3">
                  Pessoa
                </th>
                <th scope="col" className="px-5 py-3">
                  Papel
                </th>
                <th scope="col" className="px-5 py-3">
                  Situação
                </th>
                <th scope="col" className="px-5 py-3">
                  Acessos
                </th>
                <th scope="col" className="px-5 py-3 text-right">
                  Último acesso
                </th>
                <th scope="col" className="px-5 py-3" />
              </tr>
            </thead>

            <tbody>
              {data.items.map((member) => {
                const isSelf = member.id === current?.id;
                const desativado = member.status === 'suspended';
                const status = STATUS_STYLE[member.status];
                const liberados = CAIXINHAS.filter((key) => member.permissions[key]);

                return (
                  <tr key={member.id} className="border-t border-line">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        <Avatar name={member.name} url={member.avatarUrl} size={32} />
                        <div className="min-w-0">
                          <p className="truncate text-[14px] font-medium">
                            {member.name}
                            {isSelf ? (
                              <span className="ml-1.5 text-[12px] text-ink-3">(você)</span>
                            ) : null}
                          </p>
                          <p className="truncate font-mono text-[11px] text-ink-3">
                            {member.email}
                          </p>
                        </div>
                      </div>
                    </td>

                    <td className="px-5 py-3">
                      <Badge tone={ROLE_TONE[member.role]}>{ROLE_LABELS[member.role]}</Badge>
                    </td>

                    <td className="px-5 py-3">
                      <Badge tone={status.tone}>{status.label}</Badge>
                    </td>

                    <td className="px-5 py-3">
                      <span className="tabular text-[13px] text-ink-3">
                        {liberados.length} de {CAIXINHAS.length}
                      </span>
                    </td>

                    <td className="tabular px-5 py-3 text-right font-mono text-[11px] text-ink-3">
                      {member.lastLoginAt ? formatDateTime(member.lastLoginAt) : 'nunca'}
                    </td>

                    <td className="px-5 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {isSelf ? null : (
                          <>
                            {/*
                             * Ícone sozinho: o rótulo vive em `aria-label` e no
                             * `title`. São duas ações repetidas em toda linha, e
                             * a palavra ao lado de cada uma enchia a coluna sem
                             * dizer nada que a engrenagem já não diga.
                             */}
                            <Button
                              size="icon"
                              variant="ghost"
                              title={`Configurações de ${member.name}`}
                              aria-label={`Configurações de ${member.name}`}
                              onClick={() => setEditingId(member.id)}
                            >
                              <Settings {...ICON} size={16} aria-hidden />
                            </Button>

                            <Button
                              size="icon"
                              variant="ghost"
                              title={
                                desativado ? `Reativar ${member.name}` : `Desativar ${member.name}`
                              }
                              aria-label={
                                desativado ? `Reativar ${member.name}` : `Desativar ${member.name}`
                              }
                              onClick={() =>
                                update.mutate({
                                  userId: member.id,
                                  body: { status: desativado ? 'active' : 'suspended' },
                                })
                              }
                            >
                              {desativado ? (
                                <CircleCheck {...ICON} size={16} aria-hidden />
                              ) : (
                                <Ban {...ICON} size={16} aria-hidden />
                              )}
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      <CreateUserModal open={createOpen} onClose={() => setCreateOpen(false)} />
      <EditUserModal
        user={data?.items.find((item) => item.id === editingId) ?? null}
        onClose={() => setEditingId(null)}
      />
    </>
  );
}
